import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { orderApi } from '#services/order';
import {
  DEFAULT_TIME_RANGE_PRESET,
  TimeRangeValue,
  buildTimeRange,
} from '#components/time-range.utils';
import { revertAll } from '../actions';

interface BulkListState {
  filter: string;
  dateRange: TimeRangeValue;
  filterKey: string;
  headerFilters: Record<string, Record<string, boolean>>;
  order: string;
  orderBy: string;
  pageIndex: number;
  pageSize: number;
  rows: unknown[];
  rowCount: number;
  selected: Array<string | number | undefined>;
  vendors: Record<string, unknown>;
  skip: boolean;
}

const initialState: BulkListState = {
  filter: '',
  dateRange: buildTimeRange(DEFAULT_TIME_RANGE_PRESET),
  filterKey: 'bulkOrderId',
  headerFilters: {},
  order: 'desc',
  orderBy: 'id',
  pageIndex: 0,
  pageSize: 30,
  rows: [],
  rowCount: 0,
  selected: [],
  vendors: {},
  skip: false,
};

export const bulkListSlice = createSlice({
  name: 'bulkList',
  initialState,
  reducers: {
    setDateRange(state, action: PayloadAction<TimeRangeValue>) {
      state.dateRange = action.payload;
      state.pageIndex = 0;
      state.skip = false;
    },
    setFilterKey(state, action: PayloadAction<string>) {
      state.filterKey = action.payload;
      state.skip = true;
    },
    setFilter(state, action: PayloadAction<string>) {
      state.filter = action.payload;
      state.pageIndex = 0;
      state.skip = false;
    },
    setHeaderFilters(state, action: PayloadAction<Record<string, Record<string, boolean>>>) {
      state.headerFilters = action.payload;
      state.skip = false;
    },
    setOrder(state, action: PayloadAction<string>) {
      state.order = action.payload;
      state.skip = false;
    },
    setOrderBy(state, action: PayloadAction<string>) {
      state.orderBy = action.payload;
      state.skip = false;
    },
    setPageIndex(state, action: PayloadAction<number>) {
      state.pageIndex = action.payload;
      state.skip = false;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.pageIndex = 0;
      state.skip = false;
    },
    setRows(state, action: PayloadAction<unknown[]>) {
      state.rows = action.payload;
      state.skip = false;
    },
    setRowCount(state, action: PayloadAction<number>) {
      state.rowCount = action.payload;
      state.skip = false;
    },
    setSelected(state, action: PayloadAction<Array<string | number | undefined>>) {
      const { payload } = action;
      state.selected = payload;
      state.skip = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(revertAll, () => initialState)
      .addMatcher(orderApi.endpoints.getBulkOrders.matchFulfilled, (state, action) => {
        const {
          page,
          rows,
          total,
          vendors,
        } = action.payload as {
          page: number;
          rows: unknown[];
          total: number;
          vendors: Record<string, unknown>;
        };
        state.rows = rows;
        state.rowCount = total;
        state.pageIndex = page;
        state.vendors = vendors;
        // dont clear the selected data,
        // the selected states should be remembered for the crossing page selection
        // state.selected = [];
      });
  },
});

export const {
  setDateRange,
  setFilterKey,
  setFilter,
  setHeaderFilters,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setRows,
  setRowCount,
  setSelected,
} = bulkListSlice.actions;

export default bulkListSlice.reducer;
